import React, { useState, useEffect, useMemo, useContext } from 'react';
import { Alert } from 'react-bootstrap';
import Calendar from 'react-calendar';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import api from '../../api/axios'; // Use centralized api instance
import AuthContext from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FaCar, FaStar, FaSyncAlt, FaSearch, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

import ThemeContext from '../../context/ThemeContext';
import DailySummaryModal from '../../components/DailySummaryModal';
// RevisitStats replaced with inline RevisitCompact
import './DashboardPage.css';
import { hasPermission } from '../../utils/permissions';

// Helper function to format a date to YYYY-MM-DD in local time
const toYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Set Chart.js default color
ChartJS.defaults.color = '#c9d1d9';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend);

// 재방문 현황 — 세로 컴팩트
const RevisitCompact = () => {
    const [rv, setRv] = useState(null);
    const { user } = useContext(AuthContext);
    useEffect(() => {
        if (!user) return;
        api.get('/dashboard/revisit-stats').then(r => setRv(r.data.data)).catch(() => {});
    }, [user]);
    if (!rv) return <div style={{ padding: '12px', fontSize: '11px', color: '#9CA3AF', textAlign: 'center' }}>로딩중...</div>;
    const items = [
        { label: '총 등록', value: `${rv.totalVehicles}대`, icon: '🚗' },
        ...(rv.revisits || []).map(r => ({
            label: `${r.visits}회+`, value: `${r.vehicleCount}대`, pct: `${r.rate}%`,
            icon: r.visits === 2 ? '✌️' : r.visits === 3 ? '🥉' : '🏆'
        }))
    ];
    return (
        <div className="rv-compact-list">
            {items.map((item, i) => (
                <div key={i} className="rv-compact-item">
                    <span className="rv-compact-icon">{item.icon}</span>
                    <span className="rv-compact-label">{item.label}</span>
                    {item.pct && <span className="rv-compact-pct">{item.pct}</span>}
                    <span className="rv-compact-value">{item.value}</span>
                </div>
            ))}
        </div>
    );
};

const DashboardPage = () => {
    const navigate = useNavigate();
    const { theme } = useContext(ThemeContext);
    const { user } = useContext(AuthContext);
    const [stats, setStats] = useState(null);
    const [shopSettings, setShopSettings] = useState(null);
    const [error, setError] = useState('');
    
    const [activeDate, setActiveDate] = useState(new Date());

    // State for Daily Summary Modal
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    const [summaryData, setSummaryData] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);

    const [todayJobs, setTodayJobs] = useState([]);
    const [weeklyRevenueRaw, setWeeklyRevenueRaw] = useState([]);

    const canViewStats = hasPermission(user?.role, 'stats:view');

    // Fetch today's jobs for right panel
    useEffect(() => {
        if (!user || !canViewStats) return;
        const today = toYYYYMMDD(new Date());
        api.get('/jobs', { params: { startDate: today, endDate: today } })
            .then(res => setTodayJobs(res.data.data || []))
            .catch(() => {});
    }, [user, canViewStats]);

    // 주별 매출 — 월 경계 포함한 주 단위로 확장 범위 조회
    useEffect(() => {
        if (!user || !canViewStats) return;
        const y = activeDate.getFullYear(), m = activeDate.getMonth();
        const firstOfMonth = new Date(y, m, 1);
        const lastOfMonth = new Date(y, m + 1, 0);
        // 1일이 속한 주의 월요일
        const firstDay = firstOfMonth.getDay(); // 0=일 ~ 6=토
        const weekStart = new Date(firstOfMonth);
        weekStart.setDate(firstOfMonth.getDate() - (firstDay === 0 ? 6 : firstDay - 1));
        // 말일이 속한 주의 일요일
        const lastDay = lastOfMonth.getDay();
        const weekEnd = new Date(lastOfMonth);
        weekEnd.setDate(lastOfMonth.getDate() + (lastDay === 0 ? 0 : 7 - lastDay));

        api.get('/dashboard/weekly-revenue', {
            params: { startDate: toYYYYMMDD(weekStart), endDate: toYYYYMMDD(weekEnd) }
        }).then(res => setWeeklyRevenueRaw(res.data.data || []))
          .catch(() => setWeeklyRevenueRaw([]));
    }, [activeDate, user, canViewStats]);

    // Fetch monthly stats and shop settings
    useEffect(() => {
        const fetchDashboardData = async () => {
            if (!user || !canViewStats) {
                setError('통계 데이터를 조회할 권한이 없습니다.');
                return;
            }
            
            const startDate = new Date(activeDate.getFullYear(), activeDate.getMonth(), 1);
            const endDate = new Date(activeDate.getFullYear(), activeDate.getMonth() + 1, 0);
            
            try {
                // Fetch stats and settings in parallel
                const [statsResponse, settingsResponse] = await Promise.all([
                    api.get('/dashboard/stats', {
                        params: {
                            startDate: toYYYYMMDD(startDate),
                            endDate: toYYYYMMDD(endDate),
                        },
                        timeout: 10000
                    }),
                    api.get('/shops/settings')
                ]);
                
                setStats(statsResponse.data.data);
                setShopSettings(settingsResponse.data.data);

            } catch (err) {
                setError('대시보드 데이터를 불러오는 중 오류가 발생했습니다.');
                console.error('DashboardPage - Error fetching data:', err);
            }
        };
        fetchDashboardData();
    }, [activeDate, user, canViewStats]);

    // Fetch daily summary when modal is opened
    useEffect(() => {
        if (!showSummaryModal || !selectedDate) return;

        const fetchSummary = async () => {
            try {
                const dateString = toYYYYMMDD(selectedDate);
                const response = await api.get('/dashboard/daily-summary', {
                    params: { date: dateString }
                });
                setSummaryData(response.data.data);
            } catch (err) {
                console.error("Error fetching daily summary:", err);
                setError('일일 정산 데이터를 불러오는 데 실패했습니다.');
                setShowSummaryModal(false); // Close modal on error
            }
        };

        fetchSummary();
    }, [showSummaryModal, selectedDate]);

    const handleActiveStartDateChange = ({ activeStartDate }) => {
        setActiveDate(activeStartDate);
    };

    const handleDayDoubleClick = (date) => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        navigate('/admin/records', { state: { initialDate: dateString } });
    };

    const handleShowSummary = (date, e) => {
        e.stopPropagation(); // Prevent double-click from firing
        setSelectedDate(date);
        setSummaryData(null); // Reset previous data while loading
        setShowSummaryModal(true);
    };

    // --- Chart Theming ---
    const chartTextColor = useMemo(() => (theme === 'dark' ? '#c9d1d9' : '#212529'), [theme]);
    
    useEffect(() => {
        ChartJS.defaults.color = chartTextColor;
    }, [chartTextColor]);

    const chartOptions = useMemo(() => {
        const gridColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
        return {
            responsive: true, maintainAspectRatio: false,
            plugins: { legend: { labels: { color: chartTextColor } } },
            scales: {
                x: { ticks: { color: chartTextColor }, grid: { color: gridColor } },
                y: { 
                    ticks: { color: chartTextColor }, 
                    grid: { color: gridColor },
                    beginAtZero: true
                }
            }
        };
    }, [theme, chartTextColor]);

    // --- Memoized Data for Cards & Charts ---
    const summaryStats = useMemo(() => {
        if (!stats) return { revenue: 0, totalServices: 0, new: 0, returning: 0 };
        const revenue = stats.dailyRevenue.reduce((acc, item) => acc + (parseFloat(item.dailyRevenue) || 0), 0);
        const newCustomers = stats.newVsReturning.reduce((acc, item) => acc + parseInt(item.new_vehicles, 10), 0);
        const returningCustomers = stats.newVsReturning.reduce((acc, item) => acc + parseInt(item.returning_vehicles, 10), 0);
        return {
            revenue,
            totalServices: newCustomers + returningCustomers,
            new: newCustomers,
            returning: returningCustomers,
        };
    }, [stats]);

    const weeklyRevenueData = useMemo(() => {
        // activeDate가 속한 달을 기준으로 "1일이 속한 주 ~ 말일이 속한 주"를 4~6개 주 단위로 집계.
        // 월 경계(전월/다음월) 날짜 매출도 해당 주에 합산됨.
        if (!weeklyRevenueRaw.length) return { labels: [], datasets: [] };

        const y = activeDate.getFullYear(), m = activeDate.getMonth();
        const firstOfMonth = new Date(y, m, 1);
        const firstDow = firstOfMonth.getDay(); // 0=일~6=토
        // 기준 달의 1일이 속한 주의 "월요일" 날짜 (전월 포함 가능)
        const baseMonday = new Date(firstOfMonth);
        baseMonday.setDate(firstOfMonth.getDate() - (firstDow === 0 ? 6 : firstDow - 1));

        const weeks = {};
        const weekOrder = [];
        weeklyRevenueRaw.forEach(item => {
            const d = new Date(item.date);
            // 해당 날짜가 속한 주의 월요일
            const dow = d.getDay();
            const monday = new Date(d);
            monday.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
            const weekIdx = Math.round((monday - baseMonday) / (1000 * 60 * 60 * 24 * 7)) + 1;
            const label = `${m + 1}월 ${weekIdx}주차`;
            if (!weeks[label]) {
                weeks[label] = 0;
                weekOrder.push(label);
            }
            weeks[label] += (parseFloat(item.dailyRevenue) || 0);
        });

        return {
            labels: weekOrder,
            datasets: [{
                label: '주별 매출',
                data: weekOrder.map(k => weeks[k]),
                borderColor: '#1B73E8',
                backgroundColor: 'rgba(27, 115, 232, 0.3)',
                tension: 0.3
            }],
        };
    }, [weeklyRevenueRaw, activeDate]);
    
    const tileContent = ({ date, view }) => {
        if (view === 'month' && stats) {
            const dateStr = toYYYYMMDD(date);
            const dayRevenueData = stats.dailyRevenue.find(d => d.date.startsWith(dateStr));
            const dayVisitorData = stats.newVsReturning.find(d => d.date.startsWith(dateStr));
            const hasData = dayRevenueData || dayVisitorData;
            return (
                <div className="calendar-tile-content" onDoubleClick={() => handleDayDoubleClick(date)}>
                    {hasData && (
                        <div className="summary-icon" onClick={(e) => handleShowSummary(date, e)}>
                            <FaSearch />
                        </div>
                    )}
                    {dayRevenueData && <p className="revenue">{parseInt(dayRevenueData.dailyRevenue || 0).toLocaleString()}원</p>}
                    {dayVisitorData && <p className="visitors">신규:{dayVisitorData.new_vehicles}/재:{dayVisitorData.returning_vehicles}</p>}
                </div>
            );
        }
        return null;
    };

    // 오늘 매출 계산 (Hook은 조건부 return 위에)
    const todayStr = toYYYYMMDD(new Date());
    const todayRevenue = useMemo(() => {
        if (!stats?.dailyRevenue) return 0;
        const todayData = stats.dailyRevenue.find(d => d.date && d.date.startsWith(todayStr));
        return todayData ? parseFloat(todayData.dailyRevenue || 0) : 0;
    }, [stats, todayStr]);
    const retRate = summaryStats.totalServices > 0 ? Math.round(summaryStats.returning / summaryStats.totalServices * 100) : 0;

    if (error && !showSummaryModal) return <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>;
    if (!stats || !shopSettings) return <p>데이터를 불러오는 중입니다...</p>;

    // Dynamically create line chart options to set max value
    const lineChartOptions = {
        ...chartOptions,
        scales: {
            ...chartOptions.scales,
            y: {
                ...chartOptions.scales.y,
                beginAtZero: true,
            }
        }
    };

    if (weeklyRevenueData.datasets.length > 0 && weeklyRevenueData.datasets[0].data.length > 0) {
        const maxValue = Math.max(...weeklyRevenueData.datasets[0].data);
        lineChartOptions.scales.y.max = Math.ceil((maxValue * 1.25) / 100000) * 100000;
    }

    return (
        <div className="db-page">
            {/* === 좌(모바일 상단): 오늘 현황 + 인기 메뉴 + 최근 작업 === */}
            <div className="db-left">
                <div className="db-today-card">
                    <div className="db-today-header">📌 오늘 ({new Date().getMonth() + 1}/{new Date().getDate()})</div>
                    <div className="db-today-revenue">
                        <div className="db-today-amount">{todayRevenue.toLocaleString()}원</div>
                        <div className="db-today-label">오늘 매출</div>
                    </div>
                    <div className="db-today-stats">
                        <div className="db-today-stat">
                            <span>예약</span>
                            <strong>{todayJobs.filter(j => ['pending','confirmed'].includes(j.status)).length}건</strong>
                        </div>
                        <div className="db-today-stat">
                            <span>완료</span>
                            <strong style={{ color: '#0D9F6E' }}>{todayJobs.filter(j => j.status === 'completed').length}건</strong>
                        </div>
                        <div className="db-today-stat">
                            <span>진행</span>
                            <strong style={{ color: '#1B73E8' }}>{todayJobs.filter(j => j.status === 'in_progress').length}건</strong>
                        </div>
                        <div className="db-today-stat">
                            <span>대기</span>
                            <strong style={{ color: '#F59E0B' }}>{todayJobs.filter(j => j.status === 'confirmed').length}건</strong>
                        </div>
                    </div>
                    <div className="db-today-menus">
                        <div className="db-today-menus-h">인기 메뉴</div>
                        {(() => {
                            const menuCount = {};
                            todayJobs.forEach(j => { const n = j.serviceName || j.menuName; if (n) menuCount[n] = (menuCount[n] || 0) + 1; });
                            const sorted = Object.entries(menuCount).sort((a, b) => b[1] - a[1]).slice(0, 5);
                            return sorted.length > 0 ? sorted.map(([name, cnt], i) => (
                                <div key={i} className="db-today-menu-item">
                                    <span>{name}</span><strong>{cnt}건</strong>
                                </div>
                            )) : <div style={{ padding: '8px 0', color: '#9CA3AF', fontSize: '11px' }}>데이터 없음</div>;
                        })()}
                    </div>
                </div>

                <div className="db-recent-card">
                    <div className="db-recent-header">최근 작업</div>
                    {todayJobs
                        .filter(j => j.status !== 'canceled' && j.status !== 'noshow')
                        .sort((a, b) => new Date(b.startTime || b.created_at) - new Date(a.startTime || a.created_at))
                        .slice(0, 10)
                        .map((j, i) => (
                        <div key={i} className="db-recent-item">
                            <div>
                                <strong>{j.customerName || j.plateNumber || '-'}</strong>
                                <div style={{ fontSize: '10px', color: '#9CA3AF' }}>
                                    {j.startTime ? new Date(j.startTime).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }) : ''} · {j.serviceName || j.menuName || '미지정'}
                                </div>
                            </div>
                            <span style={{ fontWeight: 600, color: j.status === 'completed' ? '#0D9F6E' : j.status === 'in_progress' ? '#1B73E8' : '#F59E0B', fontSize: '11px' }}>
                                {j.status === 'completed' ? '완료' : j.status === 'in_progress' ? '진행' : '대기'}
                            </span>
                        </div>
                    ))}
                    {todayJobs.filter(j => j.status !== 'canceled' && j.status !== 'noshow').length === 0 && (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#9CA3AF', fontSize: '12px' }}>오늘 작업 없음</div>
                    )}
                </div>
            </div>

            {/* === 우(모바일 하단): 캘린더 → 월 현황 → 주별 매출 → 재방문 === */}
            <div className="db-right-col">
                <div className="db-center-card dashboard-calendar">
                    <div className="db-center-card-h db-calendar-nav">
                        <div className="db-calendar-nav-controls">
                            <button
                                type="button"
                                className="db-nav-btn"
                                onClick={() => setActiveDate(new Date(activeDate.getFullYear(), activeDate.getMonth() - 1, 1))}
                                aria-label="이전 달"
                            >
                                <FaChevronLeft />
                            </button>
                            <span className="db-calendar-title">{activeDate.getFullYear()}년 {activeDate.getMonth() + 1}월</span>
                            <button
                                type="button"
                                className="db-nav-btn"
                                onClick={() => setActiveDate(new Date(activeDate.getFullYear(), activeDate.getMonth() + 1, 1))}
                                aria-label="다음 달"
                            >
                                <FaChevronRight />
                            </button>
                            <button
                                type="button"
                                className="db-today-btn"
                                onClick={() => setActiveDate(new Date())}
                            >
                                오늘
                            </button>
                        </div>
                        <span style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 400 }}>더블클릭: 상세 / 🔍 일일정산</span>
                    </div>
                    <div className="db-center-card-b">
                        <Calendar
                            onActiveStartDateChange={handleActiveStartDateChange}
                            value={null}
                            activeStartDate={activeDate}
                            tileContent={tileContent}
                            formatDay={(locale, date) => date.getDate()}
                            showNavigation={false}
                            view="month"
                            minDetail="month"
                        />
                    </div>
                </div>

                <div className="db-below-calendar">
                    <div>
                        <div className="db-sidebar-title">{activeDate.getMonth() + 1}월 현황</div>
                        <div className="db-stat-list">
                            <div className="db-stat-item">
                                <span className="db-stat-icon" style={{ background: '#ECFDF5', color: '#0D9F6E' }}>₩</span>
                                <div className="db-stat-info">
                                    <div className="db-stat-label">매출</div>
                                    <div className="db-stat-value" style={{ color: '#0D9F6E' }}>{summaryStats.revenue.toLocaleString()}원</div>
                                </div>
                            </div>
                            <div className="db-stat-item">
                                <span className="db-stat-icon" style={{ background: '#EFF6FF', color: '#1B73E8' }}><FaCar size={14} /></span>
                                <div className="db-stat-info">
                                    <div className="db-stat-label">서비스</div>
                                    <div className="db-stat-value">{summaryStats.totalServices}건</div>
                                </div>
                            </div>
                            <div className="db-stat-item">
                                <span className="db-stat-icon" style={{ background: '#FFFBEB', color: '#F59E0B' }}><FaStar size={14} /></span>
                                <div className="db-stat-info">
                                    <div className="db-stat-label">신규</div>
                                    <div className="db-stat-value" style={{ color: '#F59E0B' }}>{summaryStats.new}건</div>
                                </div>
                            </div>
                            <div className="db-stat-item">
                                <span className="db-stat-icon" style={{ background: '#EEF2FF', color: '#6366F1' }}><FaSyncAlt size={14} /></span>
                                <div className="db-stat-info">
                                    <div className="db-stat-label">재방문</div>
                                    <div className="db-stat-value" style={{ color: '#6366F1' }}>{summaryStats.returning}건 ({retRate}%)</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="db-sidebar-card">
                        <div className="db-sidebar-card-h">주별 매출</div>
                        <div style={{ height: '200px', padding: '8px' }}>
                            <Line data={weeklyRevenueData} options={lineChartOptions} />
                        </div>
                    </div>

                    <div className="db-sidebar-card">
                        <div className="db-sidebar-card-h">재방문 현황</div>
                        <RevisitCompact />
                    </div>
                </div>
            </div>

            <DailySummaryModal
                show={showSummaryModal}
                onHide={() => setShowSummaryModal(false)}
                summaryData={summaryData}
                date={selectedDate}
            />
        </div>
    );
};

export default DashboardPage;
